import * as React from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { DownloadProgressStore } from "../stores/DownloadProgressStore";
import { useRootStore } from "../../store/contexts";

export interface DownloadProgressProps {
  store: DownloadProgressStore;
}

export const DownloadProgress: React.FC<DownloadProgressProps> = observer(
  ({ store }) => {
    const { langStore } = useRootStore();

    // Show overwrite confirmation dialog
    if (store.showOverwriteConfirm) {
      return (
        <Dialog open={true} maxWidth="sm" fullWidth>
          <DialogTitle>{langStore.downloadOverwriteTitle}</DialogTitle>
          <DialogContent>
            <Typography>
              {langStore.downloadOverwriteDescriptionWithName(store.overwriteFolderName)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => store.onOverwriteCancel?.()} color="secondary">
              {langStore.downloadOverwriteCancel}
            </Button>
            <Button
              onClick={() => store.onOverwriteConfirm?.()}
              color="primary"
              variant="contained"
            >
              {langStore.downloadOverwriteConfirm}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    // Show progress dialog
    if (!store.isDownloading || !store.progress) {
      return null;
    }

    return (
      <Dialog open={store.isDownloading} maxWidth="sm" fullWidth>
        <DialogTitle>
          {langStore.downloadProgressTitle} - {store.editionName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={store.progressPercentage}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {langStore.downloadProgressFile} {store.progress.current} {langStore.downloadProgressOf} {store.progress.total}
          </Typography>
          {store.progress.fileName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {store.progress.fileName} ({store.progress.category})
            </Typography>
          )}
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            {store.progressPercentage}%
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => store.cancelDownload()} color="secondary">
            {langStore.downloadProgressCancel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
